/**
 * Landing page content — the single source of truth for every string, icon and
 * placeholder rendered by the public marketing page (src/routes/landing/**).
 *
 * ALL copy is real French (Québec) marketing content — no lorem, no mock data.
 * Icons are stored as SVG `d` path strings (Heroicons-outline vocabulary) so the
 * `.ts` data module carries no JSX; sections render them with a shared <Icon>.
 *
 */

import type { TranslationKey } from '@/i18n';
import { tr } from '@/i18n';

export type Tone = 'indigo' | 'violet' | 'sky' | 'emerald' | 'amber' | 'rose';

/** Brand identity. */
export const BRAND = {
  name: 'Karexia',
  /** One-line positioning used in the <title>-adjacent hero + meta. */
  tagline: tr('routes.landing.landingData.theAllInOneAiPowered'),
} as const;

/** In-page anchor navigation (rendered in the sticky header + footer). */
export const NAV_LINKS: { href: string; label: string }[] = [
  { href: '#demo', label: tr('routes.landing.v2.nav.demo') },
  { href: '#produit', label: tr('routes.landing.v2.nav.product') },
  { href: '#reels', label: tr('routes.landing.v2.nav.reels') },
  { href: '#tarifs', label: tr('routes.landing.v2.nav.pricing') },
  { href: '#faq', label: tr('routes.landing.v2.nav.faq') },
  // R18 — public proof page (consented customer case studies).
  { href: '/results', label: tr('routes.landing.v2.nav.results') },
];

/** Icon path library (24×24 viewBox, stroke = currentColor). */
export const ICONS = {
  sparkles: [
    'M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z',
    'M18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456Z',
  ],
  calendar: [
    'M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5',
  ],
  users: [
    'M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z',
  ],
  robot: [
    'M9.75 9.75h.008v.008H9.75V9.75Zm4.5 0h.008v.008h-.008V9.75Z',
    'M12 3v2.25M6.75 6.75h10.5a1.5 1.5 0 0 1 1.5 1.5v7.5a1.5 1.5 0 0 1-1.5 1.5H6.75a1.5 1.5 0 0 1-1.5-1.5v-7.5a1.5 1.5 0 0 1 1.5-1.5ZM3.75 12H2.25m19.5 0h-1.5M9 17.25V19.5m6-2.25V19.5',
  ],
  chart: [
    'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z',
  ],
  megaphone: [
    'M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 1 1 0-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 0 1-1.44-4.282m3.102.069a18.03 18.03 0 0 1-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 0 1 8.835 2.535M10.34 6.66a23.847 23.847 0 0 0 8.835-2.535m0 0A23.74 23.74 0 0 0 18.795 3m.38 1.125a23.91 23.91 0 0 1 1.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 0 0 1.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 0 1 0 3.46',
  ],
  image: [
    'm2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z',
  ],
  send: [
    'M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5',
  ],
  inbox: [
    'M2.25 13.5h3.86a2.25 2.25 0 0 1 2.012 1.244l.256.512a2.25 2.25 0 0 0 2.013 1.244h3.218a2.25 2.25 0 0 0 2.013-1.244l.256-.512a2.25 2.25 0 0 1 2.013-1.244h3.859m-19.5.338V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18v-4.162c0-.224-.034-.447-.1-.661L19.24 5.338a2.25 2.25 0 0 0-2.15-1.588H6.911a2.25 2.25 0 0 0-2.15 1.588L2.35 13.177a2.25 2.25 0 0 0-.1.661Z',
  ],
  search: [
    'm21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z',
  ],
  bolt: [
    'm3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z',
  ],
  globe: [
    'M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418',
  ],
  target: [
    'M15.59 14.37a6 6 0 0 1-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 0 0 6.16-12.12A14.98 14.98 0 0 0 9.631 8.41m5.96 5.96a14.926 14.926 0 0 1-5.841 2.58m-.119-8.54a6 6 0 0 0-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 0 0-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 0 1-2.448-2.448 14.9 14.9 0 0 1 .06-.312m-2.24 2.39a4.493 4.493 0 0 0-1.757 4.306 4.493 4.493 0 0 0 4.306-1.758M16.5 9a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Z',
  ],
  shield: [
    'M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z',
  ],
  layers: [
    'm3.75 7.5 8.25-4.5 8.25 4.5-8.25 4.5-8.25-4.5Zm0 4.5 8.25 4.5 8.25-4.5m-16.5 4.5L12 21l8.25-4.5',
  ],
  chat: [
    'M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.483 4.483 0 0 1-.923 1.785A5.969 5.969 0 0 0 6 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337Z',
  ],
  play: [
    'M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z',
  ],
  check: ['m4.5 12.75 6 6 9-13.5'],
  building: [
    'M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21',
  ],
  briefcase: [
    'M20.25 14.15v4.073a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V14.15M8.25 6.75h7.5m-7.5 0V4.5a1.5 1.5 0 0 1 1.5-1.5h4.5a1.5 1.5 0 0 1 1.5 1.5v2.25m-7.5 0h7.5m-9 3.75h10.5a2.25 2.25 0 0 1 2.25 2.25v.9a48.4 48.4 0 0 1-15 0v-.9a2.25 2.25 0 0 1 2.25-2.25Z',
  ],
  rocket: [
    'M15.59 14.37a6 6 0 0 1-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 0 0 6.16-12.12A14.98 14.98 0 0 0 9.631 8.41m5.96 5.96a14.926 14.926 0 0 1-5.841 2.58m-.119-8.54a6 6 0 0 0-7.381 5.84h4.8',
  ],
} as const;

export type IconKey = keyof typeof ICONS;

/** The dense capability grid — "tout ce que Karexia fait". */
export interface Capability {
  icon: IconKey;
  label: string;
  description: string;
}

export const CAPABILITIES: Capability[] = [
  { icon: 'sparkles', label: tr('routes.landing.landingData.aiContentStudio'), description: tr('routes.landing.landingData.aiGeneratedCopyVisualsCarouselsAnd') },
  { icon: 'image', label: tr('routes.landing.landingData.creativeStudio'), description: tr('routes.landing.landingData.graphicEditorBrandTemplatesAndAnimated') },
  { icon: 'calendar', label: tr('routes.landing.landingData.editorialCalendar'), description: tr('routes.landing.landingData.visualSchedulingAndMultiNetworkPublishin') },
  { icon: 'layers', label: tr('routes.landing.landingData.queueRecycling'), description: tr('routes.landing.landingData.automaticRecyclingOfEvergreenContentAnd') },
  { icon: 'globe', label: tr('routes.landing.landingData.multiNetwork'), description: tr('routes.landing.landingData.linkedinFacebookInstagramXThreadsBluesky') },
  { icon: 'users', label: tr('routes.landing.landingData.fullCrm'), description: tr('routes.landing.landingData.contactsCompaniesGroupsPipelineAndDeals') },
  { icon: 'search', label: tr('routes.landing.landingData.b2bProspecting'), description: tr('routes.landing.landingData.multiSourceGeotargetedSearchAndEnrichmen') },
  { icon: 'target', label: tr('routes.landing.landingData.prospectRecommendations'), description: tr('routes.landing.landingData.prioritizedByProductFitAndGoal') },
  { icon: 'send', label: tr('routes.landing.landingData.outreachSequences'), description: tr('routes.landing.landingData.multichannelCampaignsEmailLinkedinSmsWha') },
  { icon: 'inbox', label: tr('routes.landing.landingData.unifiedInboxUnibox'), description: tr('routes.landing.landingData.allYourIncomingConversationsInOne') },
  { icon: 'chat', label: tr('routes.landing.landingData.communityManagement'), description: tr('routes.landing.landingData.moderationSavedRepliesAndSlaPolicies') },
  { icon: 'robot', label: tr('routes.landing.landingData.aiCopilot'), description: tr('routes.landing.landingData.anAssistantThatActsAcrossThe') },
  { icon: 'bolt', label: tr('routes.landing.landingData.n247AiTeam'), description: tr('routes.landing.landingData.specializedAgentsThatCarryOutYour') },
  { icon: 'chart', label: tr('routes.landing.landingData.advancedAnalytics'), description: tr('routes.landing.landingData.performanceRoiFunnelsCohortsAndAttributi') },
  { icon: 'megaphone', label: tr('routes.landing.landingData.advertising'), description: tr('routes.landing.landingData.adCampaignCreationAndTargeting') },
  { icon: 'briefcase', label: tr('routes.landing.landingData.servicesMarketplace'), description: tr('routes.landing.landingData.providerDirectoryBookingsAndReviews') },
  { icon: 'rocket', label: tr('routes.landing.landingData.linkInBioProjects'), description: tr('routes.landing.landingData.publicBioPagesProjectsAndApproval') },
  { icon: 'shield', label: tr('routes.landing.landingData.law25CaslCompliance'), description: tr('routes.landing.landingData.antiSpamSafeguardAndConsentEnforcement') },
];

/** Curated static pricing teaser — the HONEST fallback rendered ONLY when the
 * live, PUBLIC plans catalogue (GET /api/v1/plans) cannot be reached (network
 * failure / outage). It never states a price that could drift from the
 * catalogue: tiers are described qualitatively; every string is an i18n key
 * (namespace `adoption.plans.teaser`) so FR/EN/AR follow the visitor's locale. */
export interface TeaserPlan {
  code: string;
  nameKey: TranslationKey;
  priceKey: TranslationKey;
  taglineKey: TranslationKey;
  featureKeys: TranslationKey[];
  highlighted: boolean;
}

export const TEASER_PLANS: TeaserPlan[] = [
  {
    code: 'free',
    nameKey: 'adoption.plans.teaser.freeName',
    priceKey: 'adoption.plans.teaser.freePrice',
    taglineKey: 'adoption.plans.teaser.freeTagline',
    featureKeys: [
      'adoption.plans.teaser.featureCredits',
      'adoption.plans.teaser.featureNetworks',
      'adoption.plans.teaser.featureCopilot',
    ],
    highlighted: false,
  },
  {
    code: 'founder',
    nameKey: 'adoption.plans.teaser.founderName',
    priceKey: 'adoption.plans.teaser.founderPrice',
    taglineKey: 'adoption.plans.teaser.founderTagline',
    featureKeys: [
      'adoption.plans.teaser.featureCredits',
      'adoption.plans.teaser.featureNetworks',
      'adoption.plans.teaser.featureAiTeam',
      'adoption.plans.teaser.featureCopilot',
    ],
    highlighted: true,
  },
  {
    code: 'pro',
    nameKey: 'adoption.plans.teaser.proName',
    priceKey: 'adoption.plans.teaser.proPrice',
    taglineKey: 'adoption.plans.teaser.proTagline',
    featureKeys: [
      'adoption.plans.teaser.featureCredits',
      'adoption.plans.teaser.featureNetworks',
      'adoption.plans.teaser.featureAiTeam',
      'adoption.plans.teaser.featureSeats',
    ],
    highlighted: false,
  },
];

/** Target-persona cards (cibles) — pains → gains. */
export interface Persona {
  icon: IconKey;
  tone: Tone;
  title: string;
  summary: string;
  pains: string[];
  gains: string[];
}

export const PERSONAS: Persona[] = [
  {
    icon: 'megaphone',
    tone: 'indigo',
    title: tr('routes.landing.landingData.marketingAgencies'),
    summary: tr('routes.landing.landingData.manageMultipleClientsAndBrandsWithout'),
    pains: [tr('routes.landing.landingData.jugglingTenToolsPerClient'), tr('routes.landing.landingData.producingContentAtScale'), tr('routes.landing.landingData.provingRoiToClients')],
    gains: [tr('routes.landing.landingData.contentAndCalendarCentralizedPerBrand'), tr('routes.landing.landingData.assemblyLineAiProduction'), tr('routes.landing.landingData.clearPerformanceReports')],
  },
  {
    icon: 'building',
    tone: 'violet',
    title: tr('routes.landing.landingData.smallMediumBusinesses'),
    summary: tr('routes.landing.landingData.aProfessionalPresenceEvenWithoutA'),
    pains: [tr('routes.landing.landingData.lackOfTimeAndResources'), tr('routes.landing.landingData.hardToStayConsistent'), tr('routes.landing.landingData.tediousProspecting')],
    gains: [tr('routes.landing.landingData.theAiTeamPublishesAndProspects'), tr('routes.landing.landingData.consistencyGuaranteed'), tr('routes.landing.landingData.newCustomersWithoutManualEffort')],
  },
  {
    icon: 'users',
    tone: 'sky',
    title: tr('routes.landing.landingData.consultantsCreators'),
    summary: tr('routes.landing.landingData.buildYourPersonalBrandAndConvert'),
    pains: [tr('routes.landing.landingData.creatingContentContinuously'), tr('routes.landing.landingData.turningTheAudienceIntoCustomers'), tr('routes.landing.landingData.stayingVisibleWithoutSpendingAllDay')],
    gains: [tr('routes.landing.landingData.aiGhostwriterInYourVoice'), tr('routes.landing.landingData.linkInBioAndLeadCapture'), tr('routes.landing.landingData.dataDrivenGrowth')],
  },
  {
    icon: 'briefcase',
    tone: 'emerald',
    title: tr('routes.landing.landingData.firmsProfessionalServices'),
    summary: tr('routes.landing.landingData.growYourClientBaseWithFull'),
    pains: [tr('routes.landing.landingData.reputationAndComplianceToProtect'), tr('routes.landing.landingData.scatteredClientFollowUp'), tr('routes.landing.landingData.irregularBusinessDevelopment')],
    gains: [tr('routes.landing.landingData.law25CaslCompliantSafeguard'), tr('routes.landing.landingData.structuredCrmAndFollowUps'), tr('routes.landing.landingData.targetedMeasurableB2bProspecting')],
  },
];

export const PERSONAS_SECTION = {
  eyebrow: tr('routes.landing.landingData.whoItSFor'),
  title: tr('routes.landing.landingData.designedForTheWayYouWork'),
  subtitle: tr('routes.landing.landingData.karexiaAdaptsToTheWayYou'),
  painsLabel: tr('routes.landing.landingData.theChallenge'),
  gainsLabel: tr('routes.landing.landingData.withKarexia'),
} as const;

/** Trust / proof band — honest capability claims, not fabricated testimonials.
 * The supported-network count is NOT stored here: SocialProof derives it live
 * from the publisher capability registry (GET
 * /api/v1/publishing/public/capabilities-summary) — R18, no stale "6". */
export interface ProofStat {
  value: string;
  label: string;
}

/** Shown in the network slot only if the live capability summary is unreachable. */
export const PROOF_NETWORK_FALLBACK_VALUE = 'Multi';

export const PROOF_SECTION = {
  eyebrow: tr('routes.landing.landingData.whyKarexia'),
  title: tr('routes.landing.landingData.onePlatformInsteadOfTenTools'),
  points: [
    tr('routes.landing.landingData.replaceYourMarketingToolStackWith'),
    tr('routes.landing.landingData.handRepetitiveWorkToAiAgents'),
    tr('routes.landing.landingData.stayCompliantEverySendGoesThrough'),
  ],
} as const;

/** Footer content. `to` = in-app/public route, `href` = anchor. */
export const FOOTER = {
  tagline: tr('routes.landing.landingData.theAllInOneAiPowered2'),
  columns: [
    {
      title: tr('routes.landing.landingData.product'),
      links: [
        { label: tr('routes.landing.v2.nav.demo'), href: '#demo' },
        { label: tr('routes.landing.v2.nav.product'), href: '#produit' },
        { label: tr('routes.landing.v2.nav.reels'), href: '#reels' },
        { label: tr('routes.landing.landingData.capabilities'), href: '#capacites' },
        { label: tr('routes.landing.v2.nav.trust'), href: '#confiance' },
        { label: tr('routes.landing.v2.nav.pricing'), href: '#tarifs' },
        { label: tr('routes.landing.v2.nav.faq'), href: '#faq' },
        { label: tr('routes.landing.landingData.customerResults'), href: '/results' },
      ],
    },
    {
      title: tr('routes.landing.landingData.solutions'),
      links: [
        { label: tr('routes.landing.landingData.agencies'), href: '#cibles' },
        { label: tr('routes.landing.landingData.smallMediumBusinesses'), href: '#cibles' },
        { label: tr('routes.landing.landingData.consultants'), href: '#cibles' },
        { label: tr('routes.landing.landingData.professionalServices'), href: '#cibles' },
      ],
    },
  ],
  /** Links to real public routes. */
  legalLinks: [
    { label: tr('routes.landing.landingData.privacy'), to: '/confidentialite' },
    { label: tr('routes.landing.landingData.privacyPolicyEn'), to: '/privacy' },
    { label: tr('routes.landing.v2.trust.linkTerms'), to: '/conditions' },
    { label: tr('routes.landing.v2.trust.linkDeletion'), to: '/data-deletion' },
  ],
  contactEmail: 'info@karexia.com',
  rightsPrefix: tr('routes.landing.landingData.allRightsReserved'),
} as const;
